import { useState, useEffect, useMemo, useCallback, type FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { Loader2, User, Pencil, Trash2, Send, UserCog, Users, ArrowLeftRight, Mail, UserPlus } from "lucide-react"
import apiClient, { setAuthToken } from "@/lib/api/apiClient"
import { parseVerifyUserResponse } from "@/lib/api/userService"
import { useAppDispatch } from "@/app/hooks"
import { setAuthFromToken } from "@/features/auth/authSlice"
import { STORAGE_KEYS } from "@/constants/storageKeys"
import { useUserRole } from "@/hooks/useUserRole"
import { useIsMobile } from "@/hooks/useIsMobile"
import { showError, showSuccess, showApiError, formatApiError } from "@/lib/utils/notifications"
import { isCompanyDuplicateAddressCancelledError } from "@/features/company/utils/companyDuplicateAddress"
import { getFilterValueAngularStyle, mergeAngularFullFormParityFields } from "../../utils/companyValidators"
import { ConfirmDialog } from "@/components/ui/ConfirmDialog"
import { companyEditConfirmPresentation } from "@/features/company/constants"
import { RoleTransferModal } from "../RoleTransferModal"
import { AddUserModal } from "../AddUserModal"
import { SendEmailModal } from "../SendEmailModal"
import { CommunicationLogModal } from "../CommunicationLogModal"
import { shouldShowStaffListActionColumn } from "@/lib/utils/staffListActionColumn"
import { navigateToUserManagementEdit, navigateToUserManagementList } from "@/lib/utils/userManagementNavigation"
import { getPostImpersonateReactPath } from "@/lib/utils/postImpersonateRedirect"
import { clearImpersonationSessionFilters } from "@/lib/auth/impersonationSessionFilters"
import { getCurrentPathForImpersonateReturn } from "@/lib/utils/impersonateReturnPath"
import { getRoleDisplayName } from "@/constants/roles"
import { SectionCard } from "@/components/ui/SectionCard"
import {
  primaryEntityActionButtonClass,
  secondaryEntityActionButtonClass,
} from "@/components/ui/entityActionButtonClasses"
import { cn } from "@/lib/utils/helpers"
import tabStyles from "../../styles/companyInfoTab.module.css"

import type { CompanyStaffMember, ContactsTabProps } from "../../types"
import { formatDate } from "@/lib/utils/formatters"

/* Phone format helper: "1234567890" → "(123) 456-7890" */
function formatPhone(raw: string | undefined | null): string {
    if (!raw) return ""
    const digits = raw.replace(/\D/g, "")
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    if (digits.length === 11 && digits.startsWith("1")) {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
    }
    return raw
}

/* Format Last Activity date to MM/DD/YYYY HH:mm:ss */
function formatLastActiveDate(dateVal: string | number | null | undefined): string {
    if (!dateVal) return ""
    return formatDate(dateVal, "MM/dd/yyyy HH:mm:ss")
}

export function ContactsTab({ company, companyId: companyIdProp, onUpdate }: ContactsTabProps) {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const isMobile = useIsMobile()
    const { hasPolicy, isLibertyRole, hasWriteAccess, roleName } = useUserRole()
    const companyId = companyIdProp ?? company?.company_id ?? company?.id

    /** Angular `staff-list` Action column — company edit passes `company_id` into staff list. */
    const canShowStaffActions = shouldShowStaffListActionColumn(
        roleName,
        hasWriteAccess("user_management"),
        companyId,
    )

    const canImpersonate = hasPolicy(["user_management_impersonate_pmc_role"])

    // ── Data state ──
    const [staffList, setStaffList] = useState<CompanyStaffMember[]>([])
    const [staffLoading, setStaffLoading] = useState(true)

    // ── Dialog state ──
    const [removeConfirm, setRemoveConfirm] = useState<{ open: boolean; staff: CompanyStaffMember | null }>({ open: false, staff: null })
    const [blockRemoveOpen, setBlockRemoveOpen] = useState(false)
    const [deleteUserConfirm, setDeleteUserConfirm] = useState<{ open: boolean; userId: number | null }>({
        open: false,
        userId: null,
    })
    const [deleteOrDisableConfirm, setDeleteOrDisableConfirm] = useState<{
        open: boolean
        type: 0 | 1 | 2
        userId: number | null
    }>({ open: false, type: 0, userId: null })
    const [welcomeConfirm, setWelcomeConfirm] = useState<{ open: boolean; staff: CompanyStaffMember | null }>({ open: false, staff: null })
    const [transferModalOpen, setTransferModalOpen] = useState(false)
    const [addUserModalOpen, setAddUserModalOpen] = useState(false)
    const [processing, setProcessing] = useState(false)
    /** Angular company-edit contacts: Email toggles checkboxes + Exit/Compose; not a redirect */
    const [showSelectBox, setShowSelectBox] = useState(false)
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
    const [composeConfirmOpen, setComposeConfirmOpen] = useState(false)
    const [emailBroadcastOpen, setEmailBroadcastOpen] = useState(false)

    const canEdit = hasPolicy(["company_contact_edit"])
    const canRemoveRole = hasPolicy(["user_management_edit_role_remove"])
    const canLogCall = hasPolicy(["company_crm_log_call"])

    // ── Communication Log Dialog state ──
    const [logCallStaff, setLogCallStaff] = useState<CompanyStaffMember | null>(null)

    const loadStaffs = useCallback(async () => {
        setStaffLoading(true)
        try {
            const res = await apiClient.get("/api/property_staff", { params: { company_id: companyId, sortBy: "desc" } })
            setStaffList(Array.isArray(res.data) ? res.data : [])
        } catch (err) {
            setStaffList([])
            showError(formatApiError(err))
        } finally {
            setStaffLoading(false)
        }
    }, [companyId])

    // ── Fetch staff list ──
    useEffect(() => {
        if (!companyId) return
        loadStaffs()
    }, [companyId, loadStaffs])

    // ── Remove user role ──
    const handleRemoveRole = async () => {
        const staff = removeConfirm.staff
        if (!staff) return
        setProcessing(true)
        setRemoveConfirm({ open: false, staff: null })
        try {
            let url = `/api/user/${staff.user_id}/roles/${staff.role_id}`
            if (staff.role_name === "ROLE_REGIONAL_MANAGER") {
                url += "?flag=deleteAll"
            }
            const res = await apiClient.delete(url)
            const remainingRoles = res.data as number
            showSuccess("User Role Removed Successfully!")
            setSelectedUserIds([])
            await loadStaffs()
            // Angular: if no roles left → Delete/Disable cascade
            if (typeof remainingRoles === "number" && remainingRoles <= 0 && staff.user_id != null) {
                setDeleteUserConfirm({ open: true, userId: Number(staff.user_id) })
            }
        } catch (err: unknown) {
            const data = (err as { response?: { data?: unknown } })?.response?.data
            showError(typeof data === "string" ? data : "Failed to remove user role")
        } finally {
            setProcessing(false)
        }
    }

    const handleDeleteUserChoice = (choice: boolean) => {
        const userId = deleteUserConfirm.userId
        setDeleteUserConfirm({ open: false, userId: null })
        if (!userId) return
        setDeleteOrDisableConfirm({ open: true, type: choice ? 1 : 2, userId })
    }

    const executeDeleteOrDisable = async () => {
        const { type, userId } = deleteOrDisableConfirm
        setDeleteOrDisableConfirm({ open: false, type: 0, userId: null })
        if (!userId) return
        setProcessing(true)
        try {
            if (type === 1) {
                await apiClient.delete(`/api/user/${userId}/delete`)
            } else {
                await apiClient.delete(`/api/user/${userId}`)
            }
            showSuccess("User Action Updated Successfully!")
            await loadStaffs()
        } catch (err) {
            showApiError(err)
        } finally {
            setProcessing(false)
        }
    }

    // ── Send welcome email ──
    const handleWelcomeEmail = async () => {
        const staff = welcomeConfirm.staff
        if (!staff) return
        setProcessing(true)
        try {
            await apiClient.get(`/api/send_welcome_email/${staff.role_id}`)
            showSuccess("Welcome Email Sent Successfully!")
        } catch (err: unknown) {
            const data = (err as { response?: { data?: unknown } })?.response?.data
            showError(typeof data === "string" ? data : "Failed to send welcome email")
        } finally {
            setProcessing(false)
            setWelcomeConfirm({ open: false, staff: null })
        }
    }

    // ── Button handlers ──
    const handleTransfer = () => {
        setTransferModalOpen(true)
    }
    /** Angular Email button only toggles `showSelectBox` — does not clear selection. */
    const toggleEmailSelectionMode = () => {
        setShowSelectBox((prev) => !prev)
    }
    const cancelEmail = () => {
        setShowSelectBox(false)
        setSelectedUserIds([])
    }
    const toggleUserSelected = (userId: number | undefined | null) => {
        if (userId == null || Number.isNaN(Number(userId))) return
        const id = Number(userId)
        setSelectedUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    }

    /** User ids that can be included in email blast (same rows as row checkboxes). */
    const selectableUserIdsForEmail = useMemo(() => {
        const ids = staffList
            .map((s: { user_id?: unknown }) => s?.user_id)
            .filter((id: unknown) => id != null && !Number.isNaN(Number(id)))
            .map((id: unknown) => Number(id))
        return Array.from(new Set(ids))
    }, [staffList])

    const allEmailRowsSelected =
        selectableUserIdsForEmail.length > 0 &&
        selectableUserIdsForEmail.every((id) => selectedUserIds.includes(id))
    const someEmailRowsSelected = selectableUserIdsForEmail.some((id) => selectedUserIds.includes(id))

    const toggleSelectAllEmail = useCallback(() => {
        if (!selectableUserIdsForEmail.length) return
        setSelectedUserIds((prev) => {
            const every = selectableUserIdsForEmail.every((id) => prev.includes(id))
            if (every) {
                return prev.filter((id) => !selectableUserIdsForEmail.includes(id))
            }
            return Array.from(new Set([...prev, ...selectableUserIdsForEmail]))
        })
    }, [selectableUserIdsForEmail])
    const handleComposeClick = () => {
        if (!selectedUserIds.length) return
        setComposeConfirmOpen(true)
    }
    const onComposeConfirmed = () => {
        setComposeConfirmOpen(false)
        setEmailBroadcastOpen(true)
    }
    const onEmailBroadcastDone = () => {
        setEmailBroadcastOpen(false)
        cancelEmail()
    }
    /** Angular `company-edit.usermanagement()`: `userlist_filter` + `/user-management` (query handoff for cross-origin). */
    const handleUserManagement = () => {
        if (company != null && companyId != null && String(companyId).trim() !== "") {
            navigateToUserManagementList(navigate, {
                scope: "company",
                companyId,
                companyName: company?.company_name,
            })
            return
        }
        navigateToUserManagementList(navigate)
    }
    const handleAddUser = () => {
        setAddUserModalOpen(true)
    }
    const handleEditUser = (userId: string | number) => {
        navigateToUserManagementEdit(navigate, userId)
    }

    /** Same API sequence as Angular `StaffListComponent.impersonateUser`; stay in React SPA + client navigate. */
    const handleImpersonate = async (roleId: string | number) => {
        try {
            const currentToken = localStorage.getItem(STORAGE_KEYS.authToken)
            if (currentToken) {
                localStorage.setItem("admin_token", currentToken)
            }
            const returnPath = getCurrentPathForImpersonateReturn()
            localStorage.setItem("impersonate_path", returnPath)
            localStorage.setItem("impersonatepath", returnPath)
            sessionStorage.setItem("impersonate_process", "1")

            const res = await apiClient.post("/api/impersonate", { role_id: roleId })
            const data = res.data as { token?: string; ott?: string }
            if (!data?.token) {
                showApiError(new Error("Impersonate response missing token."))
                return
            }

            localStorage.setItem("old_token", localStorage.getItem(STORAGE_KEYS.authToken) || "")
            setAuthToken(data.token)

            const verifyRes = await apiClient.get("/api/verify?force")
            const userData = parseVerifyUserResponse(verifyRes.data)

            dispatch(setAuthFromToken({ token: data.token, user: userData }))
            try {
                localStorage.setItem("user", JSON.stringify(userData))
            } catch {
                /* ignore */
            }
            localStorage.setItem("impersonate", "true")

            clearImpersonationSessionFilters()

            const nextPath = getPostImpersonateReactPath(userData as unknown as Record<string, unknown>, {
                fallbackCompanyId: companyId,
            })
            if (nextPath === window.location.pathname) {
                window.location.reload()
                return
            }
            navigate(nextPath, { replace: true })
        } catch (err) {
            if (localStorage.getItem("impersonate") !== "true") {
                localStorage.removeItem("admin_token")
                sessionStorage.removeItem("impersonate_process")
            }
            showApiError(err)
        }
    }

    /**
     * Angular: contacts fields (e.g. sales_team_id) are on the main companyEditForm; Save posts the whole form.
     * Do not spread full GET `company` (nested relations break validation). Same parity fields as Executive Summary tab.
     */
    const handleContactsFormSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (!onUpdate || !companyId) return
        setProcessing(true)
        try {
            const raw: Record<string, unknown> = {
                sales_team_id: String(company?.sales_team_id ?? "").trim(),
                federal_id:
                    company?.federal_id != null && company.federal_id !== ""
                        ? String(company.federal_id).trim()
                        : "",
            }
            const filtered = getFilterValueAngularStyle(raw) as Record<string, unknown>
            const payload = mergeAngularFullFormParityFields(
                company as Record<string, unknown> | undefined,
                filtered,
            )
            await onUpdate(payload)
            showSuccess("Company Updated  Successfully!")
        } catch (err: unknown) {
            if (isCompanyDuplicateAddressCancelledError(err)) return
            showError(formatApiError(err))
        } finally {
            setProcessing(false)
        }
    }

    return (
        <>
        <form
            data-testid="company-contacts-tab-form"
            data-tab-id="contacts"
            onSubmit={handleContactsFormSubmit}
            className={`${tabStyles.tabContent} animate-in fade-in duration-300 ${!canEdit ? "pointer-events-none opacity-60" : ""}`}
        >
            {canEdit && (
                <section
                    data-testid="company-contacts-section-actions"
                    className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-[0_4px_12px_rgba(15,23,42,0.05),0_1px_2px_rgba(15,23,42,0.04)] sm:px-5"
                >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                        <div className="flex shrink-0 items-center gap-2.5 lg:pr-4">
                            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#c7ddff] bg-[#dbeafe] text-[rgb(21_151_255)]">
                                <UserCog className="h-4 w-4" aria-hidden />
                            </span>
                            <h2 className="whitespace-nowrap text-base font-bold tracking-tight text-slate-900">
                                Contact Actions
                            </h2>
                        </div>

                        <div className="hidden h-9 w-px shrink-0 bg-slate-200 lg:block" aria-hidden />

                        <div className="flex w-full flex-1 flex-wrap items-center gap-2.5 lg:pl-4">
                            <button
                                data-testid="company-contacts-transfer-button"
                                type="button"
                                onClick={handleTransfer}
                                className={cn(primaryEntityActionButtonClass, "gap-2")}
                            >
                                <ArrowLeftRight className="h-4 w-4 shrink-0" aria-hidden />
                                Transfer
                            </button>
                            <button
                                data-testid="company-contacts-email-mode-button"
                                type="button"
                                onClick={toggleEmailSelectionMode}
                                className={cn(primaryEntityActionButtonClass, "gap-2")}
                            >
                                <Mail className="h-4 w-4 shrink-0" aria-hidden />
                                Email
                            </button>
                            <button
                                data-testid="company-contacts-user-management-button"
                                type="button"
                                onClick={handleUserManagement}
                                className={cn(primaryEntityActionButtonClass, "gap-2 whitespace-nowrap")}
                            >
                                <Users className="h-4 w-4 shrink-0" aria-hidden />
                                User Management
                            </button>
                            <button
                                data-testid="company-contacts-add-user-button"
                                type="button"
                                onClick={handleAddUser}
                                className={cn(primaryEntityActionButtonClass, "gap-2 whitespace-nowrap")}
                            >
                                <UserPlus className="h-4 w-4 shrink-0" aria-hidden />
                                Add User
                            </button>
                        </div>
                    </div>

                    {showSelectBox ? (
                        <div className="mt-3 flex flex-wrap items-center justify-end gap-2.5 border-t border-slate-100 pt-3">
                            <button
                                data-testid="company-contacts-compose-button"
                                type="button"
                                onClick={handleComposeClick}
                                disabled={!selectedUserIds.length}
                                className={cn(primaryEntityActionButtonClass, "gap-2")}
                            >
                                <Mail className="h-4 w-4" aria-hidden />
                                Compose
                            </button>
                            <button
                                data-testid="company-contacts-compose-cancel-button"
                                type="button"
                                onClick={cancelEmail}
                                className={secondaryEntityActionButtonClass}
                            >
                                Cancel
                            </button>
                        </div>
                    ) : null}
                </section>
            )}

            <SectionCard
                icon={Users}
                title="Contact Information"
                testId="company-contacts-section-contact-information"
            >
                {staffLoading ? (
                    <div data-testid="company-contacts-loading" className="flex items-center gap-2 py-4">
                        <Loader2 size={18} className="animate-spin text-[rgb(21_151_255)]" />
                        <span className="text-sm text-slate-500">Loading contacts…</span>
                    </div>
                ) : staffList.length === 0 ? (
                    <p data-testid="company-contacts-empty" className="text-sm text-slate-500">
                        No contacts found.
                    </p>
                ) : isMobile ? (
                    <div data-testid="company-contacts-grid" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {staffList.map((staff, i) => {
                            const rowKey = String(staff.user_id ?? staff.role_id ?? `idx-${i}`)
                            const displayName =
                                staff.name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim()
                            return (
                                <div
                                    key={staff.user_id || i}
                                    data-testid={`company-contacts-card-${rowKey}`}
                                    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                                >
                                    <div className="mb-3 flex items-start justify-between gap-2">
                                        <button
                                            type="button"
                                            data-testid={`company-contacts-card-${rowKey}-name`}
                                            className={`text-left text-sm font-semibold text-[rgb(21_151_255)] ${
                                                canLogCall ? "underline" : ""
                                            }`}
                                            onClick={() => canLogCall && setLogCallStaff(staff)}
                                        >
                                            {displayName || "—"}
                                        </button>
                                        {showSelectBox && canEdit && staff.user_id != null && (
                                            <input
                                                type="checkbox"
                                                data-testid={`company-contacts-card-${rowKey}-email-checkbox`}
                                                checked={selectedUserIds.includes(Number(staff.user_id))}
                                                onChange={() =>
                                                    toggleUserSelected(
                                                        staff.user_id != null ? Number(staff.user_id) : undefined,
                                                    )
                                                }
                                                aria-label={`Select ${displayName || "staff"}`}
                                            />
                                        )}
                                    </div>
                                    <dl className="space-y-1.5 text-xs text-slate-600">
                                        <div className="flex justify-between gap-2">
                                            <dt className="text-slate-400">Role</dt>
                                            <dd>
                                                {staff.role_name || staff.role
                                                    ? getRoleDisplayName(staff.role_name ?? staff.role)
                                                    : "—"}
                                            </dd>
                                        </div>
                                        <div className="flex justify-between gap-2">
                                            <dt className="text-slate-400">Phone</dt>
                                            <dd>{formatPhone(staff.mobile_no) || "—"}</dd>
                                        </div>
                                        <div className="flex justify-between gap-2">
                                            <dt className="text-slate-400">Email</dt>
                                            <dd className="truncate">{staff.email || "—"}</dd>
                                        </div>
                                        <div className="flex justify-between gap-2">
                                            <dt className="text-slate-400">Last Activity</dt>
                                            <dd>{formatLastActiveDate(staff.last_active) || "—"}</dd>
                                        </div>
                                    </dl>
                                    {canShowStaffActions && (
                                        <div className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
                                            {staff.role_id && isLibertyRole && canImpersonate && (
                                                <button
                                                    type="button"
                                                    title="Impersonate"
                                                    disabled={processing}
                                                    onClick={() => staff.role_id != null && handleImpersonate(staff.role_id)}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500"
                                                >
                                                    <User size={14} />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                title="Edit"
                                                onClick={() => staff.user_id != null && handleEditUser(staff.user_id)}
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            {canRemoveRole && (
                                                <button
                                                    type="button"
                                                    title="Remove"
                                                    disabled={processing}
                                                    onClick={() => {
                                                        if (staffList.length <= 1) {
                                                            setBlockRemoveOpen(true)
                                                            return
                                                        }
                                                        setRemoveConfirm({ open: true, staff })
                                                    }}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                            {!staff.user_password && !!companyId && (
                                                <button
                                                    type="button"
                                                    title="Send Welcome Email"
                                                    disabled={processing}
                                                    onClick={() => setWelcomeConfirm({ open: true, staff })}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500"
                                                >
                                                    <Send size={14} />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div
                        data-testid="company-contacts-table-wrap"
                        className="overflow-x-auto rounded-lg border border-slate-200"
                    >
                        <table data-testid="company-contacts-table" className="w-full border-collapse text-[13px]">
                            <thead data-testid="company-contacts-table-head" className="bg-slate-50/80">
                                <tr className="border-b border-slate-200">
                                    {showSelectBox && canEdit && (
                                        <th className="w-11 px-3 py-2.5 text-center text-xs font-medium text-slate-500">
                                            <input
                                                data-testid="company-contacts-select-all-checkbox"
                                                type="checkbox"
                                                disabled={selectableUserIdsForEmail.length === 0}
                                                checked={allEmailRowsSelected}
                                                onChange={toggleSelectAllEmail}
                                                aria-label="Select all contacts for email"
                                                ref={(el) => {
                                                    if (!el) return
                                                    el.indeterminate = someEmailRowsSelected && !allEmailRowsSelected
                                                }}
                                            />
                                        </th>
                                    )}
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Name</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Role</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Phone Number</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Email</th>
                                    <th className="px-3 py-2.5 text-left text-xs font-medium text-slate-500">Last Activity</th>
                                    {canShowStaffActions && (
                                        <th className="w-0 px-3 py-2.5 text-left text-xs font-medium whitespace-nowrap text-slate-500">
                                            Action
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody data-testid="company-contacts-table-body">
                                {staffList.map((staff, i) => {
                                    const rowKey = String(staff.user_id ?? staff.role_id ?? `idx-${i}`)
                                    return (
                                    <tr
                                        key={staff.user_id || i}
                                        data-testid={`company-contacts-row-${rowKey}`}
                                        className="border-b border-slate-100 last:border-b-0"
                                    >
                                        {showSelectBox && canEdit && (
                                            <td className="px-3 py-3 align-middle">
                                                {staff.user_id != null ? (
                                                    <input
                                                        data-testid={`company-contacts-row-${rowKey}-email-checkbox`}
                                                        type="checkbox"
                                                        checked={selectedUserIds.includes(Number(staff.user_id))}
                                                        onChange={() => toggleUserSelected(staff.user_id != null ? Number(staff.user_id) : undefined)}
                                                        aria-label={`Select ${staff.name ?? "staff"}`}
                                                    />
                                                ) : null}
                                            </td>
                                        )}
                                        <td
                                            data-testid={`company-contacts-row-${rowKey}-name-cell`}
                                            className={`px-3 py-3 font-medium text-[rgb(21_151_255)] ${
                                                canLogCall ? "cursor-pointer underline" : "cursor-default"
                                            }`}
                                            onClick={() => canLogCall && setLogCallStaff(staff)}
                                        >
                                            {staff.name || `${staff.first_name || ""} ${staff.last_name || ""}`.trim()}
                                        </td>
                                        <td className="px-3 py-3 text-slate-500">
                                            {staff.role_name || staff.role
                                                ? getRoleDisplayName(staff.role_name ?? staff.role)
                                                : ""}
                                        </td>
                                        <td className="px-3 py-3 text-slate-500">{formatPhone(staff.mobile_no)}</td>
                                        <td className="px-3 py-3 text-slate-500">{staff.email}</td>
                                        <td className="px-3 py-3 text-slate-500">{formatLastActiveDate(staff.last_active) || "-"}</td>
                                        {canShowStaffActions && (
                                            <td className="w-0 px-3 py-3 whitespace-nowrap">
                                                <div className="flex gap-1.5">
                                                {/* Impersonate — Angular: dashboardicon-profile + liberty_roles + user_management_impersonate_pmc_role */}
                                                {staff.role_id && isLibertyRole && canImpersonate && (
                                                    <button
                                                        data-testid={`company-contacts-row-${rowKey}-impersonate-button`}
                                                        type="button"
                                                        title="Impersonate"
                                                        disabled={processing}
                                                        onClick={() => staff.role_id != null && handleImpersonate(staff.role_id)}
                                                        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        <User size={14} />
                                                    </button>
                                                )}
                                                {/* Edit */}
                                                <button
                                                    data-testid={`company-contacts-row-${rowKey}-edit-button`}
                                                    type="button"
                                                    title="Edit"
                                                    onClick={() => staff.user_id != null && handleEditUser(staff.user_id)}
                                                    className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                {/* Delete / Remove */}
                                                {canRemoveRole && (
                                                    <button
                                                        data-testid={`company-contacts-row-${rowKey}-remove-role-button`}
                                                        type="button"
                                                        title="Remove"
                                                        onClick={() => {
                                                            if (staffList.length <= 1) {
                                                                setBlockRemoveOpen(true)
                                                                return
                                                            }
                                                            setRemoveConfirm({ open: true, staff })
                                                        }}
                                                        disabled={processing}
                                                        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                                {/* Send Welcome Email — Angular: propertyId || companyId */}
                                                {!staff.user_password && !!companyId && (
                                                    <button
                                                        data-testid={`company-contacts-row-${rowKey}-welcome-email-button`}
                                                        type="button"
                                                        title="Send Welcome Email"
                                                        onClick={() => setWelcomeConfirm({ open: true, staff })}
                                                        disabled={processing}
                                                        className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        <Send size={14} />
                                                    </button>
                                                )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </SectionCard>

            {/* ── Unable to remove last contact (Angular staff-list removeUserRole) ── */}
            <ConfirmDialog
                isOpen={blockRemoveOpen}
                title="Sorry, Unable To Remove User!"
                message="There are no other employees assigned to this company. Please add a new contact before removing this user."
                primaryLabel="OK"
                onPrimary={() => setBlockRemoveOpen(false)}
                secondaryLabel=""
                onSecondary={() => setBlockRemoveOpen(false)}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-contacts-block-remove-overlay"
                primaryButtonDataTestId="company-contacts-block-remove-ok-button"
                secondaryButtonDataTestId="company-contacts-block-remove-secondary-button"
                closeButtonDataTestId="company-contacts-block-remove-close-button"
            />

            {/* ── Remove Role Confirm Dialog ── */}
            <ConfirmDialog
                isOpen={removeConfirm.open}
                title="Confirm"
                message={
                    removeConfirm.staff?.role_name === "ROLE_REGIONAL_MANAGER"
                        ? "Regional Mgr is still connected to Properties.\nDo you want to remove ALL Property links?"
                        : "Delete User Role?"
                }
                primaryLabel="Yes"
                onPrimary={handleRemoveRole}
                secondaryLabel="No"
                onSecondary={() => setRemoveConfirm({ open: false, staff: null })}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-contacts-remove-role-confirm-overlay"
                primaryButtonDataTestId="company-contacts-remove-role-confirm-yes-button"
                secondaryButtonDataTestId="company-contacts-remove-role-confirm-no-button"
                closeButtonDataTestId="company-contacts-remove-role-confirm-close-button"
            />

            {/* ── After last role: Delete or Disable user (Angular staff-list deleteUser) ── */}
            <ConfirmDialog
                isOpen={deleteUserConfirm.open}
                title="Confirm"
                message="There are no other roles for this User. Do you want to Delete or Disable the user? Disable, if you are moving a property to be managed by a different company, and you want to keep the user, but need to update their role. Otherwise, delete them to permanently remove them from the system."
                primaryLabel="Delete"
                onPrimary={() => handleDeleteUserChoice(true)}
                secondaryLabel="Disable"
                onSecondary={() => handleDeleteUserChoice(false)}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-contacts-delete-user-choice-overlay"
                primaryButtonDataTestId="company-contacts-delete-user-choice-delete-button"
                secondaryButtonDataTestId="company-contacts-delete-user-choice-disable-button"
                closeButtonDataTestId="company-contacts-delete-user-choice-close-button"
            />

            <ConfirmDialog
                isOpen={deleteOrDisableConfirm.open}
                title={deleteOrDisableConfirm.type === 1 ? "Delete User?" : "Disable User?"}
                message={
                    deleteOrDisableConfirm.type === 1
                        ? "Are you sure want to Delete User?"
                        : "Are you sure want to Disable User"
                }
                primaryLabel="Yes"
                onPrimary={executeDeleteOrDisable}
                secondaryLabel="No"
                onSecondary={() => setDeleteOrDisableConfirm({ open: false, type: 0, userId: null })}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-contacts-delete-disable-final-overlay"
                primaryButtonDataTestId="company-contacts-delete-disable-final-yes-button"
                secondaryButtonDataTestId="company-contacts-delete-disable-final-no-button"
                closeButtonDataTestId="company-contacts-delete-disable-final-close-button"
            />

            {/* ── Welcome Email Confirm Dialog ── */}
            <ConfirmDialog
                isOpen={welcomeConfirm.open}
                title="Confirm"
                message="Do you want to send Welcome email to User?"
                primaryLabel="Yes"
                onPrimary={handleWelcomeEmail}
                secondaryLabel="No"
                onSecondary={() => setWelcomeConfirm({ open: false, staff: null })}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-contacts-welcome-email-confirm-overlay"
                primaryButtonDataTestId="company-contacts-welcome-email-confirm-yes-button"
                secondaryButtonDataTestId="company-contacts-welcome-email-confirm-no-button"
                closeButtonDataTestId="company-contacts-welcome-email-confirm-close-button"
            />

            {/* ── Role Transfer Modal (same as Angular StaffTransferComponent) ── */}
            <RoleTransferModal
                isOpen={transferModalOpen}
                onClose={() => setTransferModalOpen(false)}
                companyId={companyId ?? ""}
                companyName={company?.company_name ?? ""}
                staffList={staffList}
                onSuccess={loadStaffs}
            />

            <ConfirmDialog
                isOpen={composeConfirmOpen}
                title="Confirm"
                message={`Blast email will send to ${selectedUserIds.length} user`}
                primaryLabel="Yes"
                onPrimary={onComposeConfirmed}
                secondaryLabel="No"
                onSecondary={() => setComposeConfirmOpen(false)}
                {...companyEditConfirmPresentation}
                overlayDataTestId="company-contacts-blast-email-confirm-overlay"
                primaryButtonDataTestId="company-contacts-blast-email-confirm-yes-button"
                secondaryButtonDataTestId="company-contacts-blast-email-confirm-no-button"
                closeButtonDataTestId="company-contacts-blast-email-confirm-close-button"
            />

            {/* ── Communication Log Modal (Angular: logCall) ── */}
            <CommunicationLogModal
                isOpen={!!logCallStaff}
                onClose={() => setLogCallStaff(null)}
                pmcType="COMPANY"
                entityId={companyId ?? ""}
                staffDetail={logCallStaff}
                entityDetails={company}
            />

            {/* Header Submit → Confirm → requestSubmit() targets this form (matches Angular main form submit) */}
            <button type="submit" tabIndex={-1} aria-hidden style={{ position: "absolute", width: 0, height: 0, opacity: 0, pointerEvents: "none" }} />
        </form>

        {/* Outside <form>: nested <form> is invalid HTML; inner Submit would submit company contacts form instead of email. */}
        {emailBroadcastOpen && companyId && (
            <SendEmailModal
                selectedUserIds={selectedUserIds}
                companyId={companyId}
                onClose={() => setEmailBroadcastOpen(false)}
                onSuccess={onEmailBroadcastDone}
            />
        )}

        {/* Outside <form>: avoids Search/Enter in Add User modal submitting company form (data-tab-id="contacts"). */}
        <AddUserModal
            isOpen={addUserModalOpen}
            onClose={() => setAddUserModalOpen(false)}
            companyId={companyId ?? ""}
            onSuccess={loadStaffs}
        />
        </>
    )
}
